import { tryParseMoney, webPriceInEffect, webPriceProblem } from '@sitzmark/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNotice } from '../../components/notice.js';
import { Badge, Button, Callout, Card, Modal, Mono, Screen } from '../../components/ui.js';
import { SearchSelect } from '../../components/search-select.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { marginPercent } from './product-matrix.js';
import { BrandPicker } from './brand-picker.js';
import { DateInput } from '../../components/date-input.js';
import { tillDay } from '../../lib/till-day.js';
import { CorrectCostDialog } from './cost-correction.js';
import './products.css';

/**
 * Everything about a style that is not a price (SCOPE §5.2).
 *
 * The answer to "where do I change the name, the style number, the vendor, the
 * department?" — which had no answer at all until now. A style could be created
 * and repriced, and nothing else, so a rep's spreadsheet with the wrong name in
 * it meant deleting the style and re-keying every size of it.
 *
 * A screen rather than a modal, for the same reason New Style is one: it is a
 * form with real work in it, and `Modal` is for a question (§2.2-4).
 */

type Detail = Awaited<ReturnType<typeof api.products.get.query>>;
type Variant = Detail['variants'][number];

export function EditStyleScreen({
  product,
  onClose,
  onSaved,
  onDeleted,
}: {
  product: Detail['product'];
  onClose: () => void;
  onSaved: (detail: Detail) => void;
  onDeleted: () => void;
}) {
  const notify = useNotice();
  const taxonomy = useQuery({
    queryKey: ['products', 'taxonomy'],
    queryFn: () => api.products.taxonomy.query(),
    staleTime: 300_000,
  });

  const [draft, setDraft] = useState(() => ({
    styleNumber: product.styleNumber,
    name: product.name,
    description: product.description ?? '',
    vendorId: product.vendorId ?? '',
    brandId: product.brandId ?? '',
    departmentId: product.departmentId ?? '',
    categoryId: product.categoryId ?? '',
    taxClassId: product.taxClassId ?? '',
    season: product.season ?? '',
    trackInventory: product.trackInventory,
    finalSale: product.finalSale,
    active: product.active,
    inStoreOnly: product.inStoreOnly,
    ships: product.ships,
    surcharge:
      product.oversizeSurchargeCents === null
        ? ''
        : (product.oversizeSurchargeCents / 100).toFixed(2),
    weightLb: product.weightGrams === null ? '' : poundsFrom(product.weightGrams),
    webPrice: product.webPriceCents === null ? '' : (product.webPriceCents / 100).toFixed(2),
    webPriceStartsOn: product.webPriceStartsOn ?? '',
    webPriceEndsOn: product.webPriceEndsOn ?? '',
  }));

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const surchargeCents = draft.surcharge.trim() === '' ? null : tryParseMoney(draft.surcharge);
  const weightGrams = draft.weightLb.trim() === '' ? null : gramsFrom(draft.weightLb);
  // The model's own online price (decision 259), checked by the rule the server uses.
  const webPrice = {
    cents: draft.webPrice.trim() === '' ? null : tryParseMoney(draft.webPrice),
    startsOn: draft.webPriceStartsOn === '' ? null : draft.webPriceStartsOn,
    endsOn: draft.webPriceEndsOn === '' ? null : draft.webPriceEndsOn,
  };
  const webPriceChanged =
    webPrice.cents !== product.webPriceCents ||
    webPrice.startsOn !== product.webPriceStartsOn ||
    webPrice.endsOn !== product.webPriceEndsOn;
  const webPriceTyped =
    draft.webPrice.trim() !== '' && webPrice.cents === null
      ? 'The online price is not an amount'
      : webPriceProblem(webPrice);

  const save = useMutation({
    mutationFn: () =>
      api.products.update
        .mutate({
          productId: product.id,
          styleNumber: draft.styleNumber.trim(),
          name: draft.name.trim(),
          description: draft.description.trim() === '' ? null : draft.description.trim(),
          vendorId: draft.vendorId,
          brandId: draft.brandId === '' ? null : draft.brandId,
          departmentId: draft.departmentId,
          categoryId: draft.categoryId === '' ? null : draft.categoryId,
          taxClassId: draft.taxClassId === '' ? null : draft.taxClassId,
          season: draft.season.trim() === '' ? null : draft.season.trim(),
          trackInventory: draft.trackInventory,
          finalSale: draft.finalSale,
          active: draft.active,
          inStoreOnly: draft.inStoreOnly,
          ships: draft.ships,
          oversizeSurchargeCents: surchargeCents === 0 ? null : surchargeCents,
          weightGrams,
        })
        .then(async (detail) => {
          if (!webPriceChanged) return detail;
          await api.products.setWebPrice.mutate({ productId: product.id, ...webPrice });
          return api.products.get.query({ productId: product.id });
        }),
    onSuccess: (detail) => {
      recordBreadcrumb('products', 'edited a model');
      notify({ title: `Saved — ${detail.product.name}` });
      onSaved(detail);
    },
  });

  const remove = useMutation({
    mutationFn: () => api.products.delete.mutate({ productId: product.id }),
    onSuccess: () => {
      recordBreadcrumb('products', 'deleted a model');
      notify({ title: `Deleted — ${product.name}` });
      onDeleted();
    },
  });

  const problem =
    draft.styleNumber.trim() === ''
      ? 'Give it a model number'
      : draft.name.trim() === ''
        ? 'Give it a name'
        : draft.vendorId === ''
          ? 'Pick a vendor'
          : draft.departmentId === ''
            ? 'Pick a category'
            : draft.surcharge.trim() !== '' && surchargeCents === null
              ? 'The extra shipping is not an amount'
              : draft.weightLb.trim() !== '' && weightGrams === null
                ? 'The weight is not a number of pounds'
                : (webPriceTyped ?? undefined);

  return (
    <Screen
      title={`Edit ${product.name}`}
      subtitle="Changing the model number or vendor does not rewrite SKUs already printed on labels."
      destructiveAction={
        <Button variant="danger" onClick={() => setConfirmingDelete(true)}>
          Delete model
        </Button>
      }
      secondaryActions={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          size="large"
          busy={save.isPending}
          disabledReason={problem}
          onClick={() => save.mutate()}
        >
          Save changes
        </Button>
      }
    >
      <Card title="The model">
        <div className="style-form">
          <Field
            label="Model number"
            value={draft.styleNumber}
            onChange={(value) => setDraft({ ...draft, styleNumber: value })}
            mono
          />
          <Field
            label="Name"
            value={draft.name}
            onChange={(value) => setDraft({ ...draft, name: value })}
          />
          <Picker
            label="Vendor"
            searchable
            value={draft.vendorId}
            onChange={(value) => setDraft({ ...draft, vendorId: value })}
            options={(taxonomy.data?.vendors ?? []).map((vendor) => ({
              value: vendor.id,
              label: vendor.name,
            }))}
          />
          <BrandPicker
            value={draft.brandId}
            onChange={(value) => setDraft({ ...draft, brandId: value })}
            optional
            brands={taxonomy.data?.brands ?? []}
            vendorName={
              taxonomy.data?.vendors.find((vendor) => vendor.id === draft.vendorId)?.name ?? null
            }
          />
          {/*
            Where it is filed, at any level, read as a path — and its gender,
            from the store's one list, independent of the category (decision
            214). A helmet has no gender, so it is optional.
          */}
          <Picker
            label="Category"
            searchable
            value={draft.departmentId}
            onChange={(value) => setDraft({ ...draft, departmentId: value })}
            options={(taxonomy.data?.departments ?? []).map((category) => ({
              value: category.id,
              label: category.path,
            }))}
          />
          {/* A store whose categories say who it is for keeps no genders (decision 224). */}
          {(taxonomy.data?.genders ?? []).length > 0 ? (
            <Picker
              label="Gender"
              value={draft.categoryId}
              optional
              onChange={(value) => setDraft({ ...draft, categoryId: value })}
              options={(taxonomy.data?.genders ?? []).map((gender) => ({
                value: gender.id,
                label: `${gender.name}${gender.group === null ? '' : ` · ${gender.group}`}`,
              }))}
            />
          ) : null}
          <Field
            label="Season"
            value={draft.season}
            placeholder="FA26"
            onChange={(value) => setDraft({ ...draft, season: value })}
          />
          <Picker
            label="Tax class"
            value={draft.taxClassId}
            optional
            onChange={(value) => setDraft({ ...draft, taxClassId: value })}
            options={(taxonomy.data?.taxClasses ?? []).map((taxClass) => ({
              value: taxClass.id,
              label: taxClass.name,
            }))}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="edit-style-description">
            Description
          </label>
          <textarea
            id="edit-style-description"
            className="input textarea"
            rows={2}
            value={draft.description}
            onChange={(event) => setDraft({ ...draft, description: event.target.value })}
          />
        </div>
      </Card>

      <Card title="Availability">
        <label className="toggle">
          <input
            type="checkbox"
            checked={!draft.trackInventory}
            onChange={(event) => setDraft({ ...draft, trackInventory: !event.target.checked })}
          />
          <span>This is a service, not stock — priced and reported, never counted</span>
        </label>

        <label className="toggle">
          <input
            type="checkbox"
            checked={draft.finalSale}
            onChange={(event) => setDraft({ ...draft, finalSale: event.target.checked })}
          />
          <span>
            Final sale — marked down and sold as-is. Receipts print the final-sale policy for these
            lines instead of the standard one; already-printed receipts keep whatever was true when
            they sold.
          </span>
        </label>

        <label className="toggle">
          <input
            type="checkbox"
            checked={!draft.active}
            onChange={(event) => setDraft({ ...draft, active: !event.target.checked })}
          />
          <span>
            Inactive — off the register and out of search, with its history left intact. This is
            what to use for last season's stock rather than deleting it.
          </span>
        </label>
      </Card>

      {/*
        Web shop and shipping, per style (decisions 152, 204). Checkout always
        honored "does not ship" and the extra per unit; nothing could set them
        until the client asked: "we need to be able to set the shipping charge
        for all items, some stuff is very expensive to ship" (2026-10-15).
      */}
      <Card title="Web shop and shipping">
        <label className="toggle">
          <input
            type="checkbox"
            checked={draft.inStoreOnly}
            onChange={(event) => setDraft({ ...draft, inStoreOnly: event.target.checked })}
          />
          <span>
            In store only — shown on the web shop, but not sold there. Its page says it is available
            in store and offers no cart.
          </span>
        </label>
        <label className="toggle">
          <input
            type="checkbox"
            checked={!draft.ships}
            onChange={(event) => setDraft({ ...draft, ships: !event.target.checked })}
          />
          <span>Pickup only — never shipped, like wax or anything that cannot fly</span>
        </label>
        <div className="style-form">
          <Field
            label="Extra shipping, each"
            value={draft.surcharge}
            placeholder="0.00"
            onChange={(value) => setDraft({ ...draft, surcharge: value })}
            hint="Added to shipping for every one in an order — skis, boards, anything costly to ship."
          />
          <Field
            label="Weight (lb)"
            value={draft.weightLb}
            placeholder="2.5"
            onChange={(value) => setDraft({ ...draft, weightLb: value })}
            hint="For shipping charged by weight. Leave blank if unknown."
          />
        </div>
        {/*
          Its own price online (decision 259; client, 2026-10-27: "a different
          online price per model … store price is never shown online if an
          online price is set or exists (if its blank its the same as
          instore)"; "start end dates are good").
        */}
        <div className="style-form">
          <Field
            label="Online price"
            value={draft.webPrice}
            placeholder="Same as in store"
            onChange={(value) => setDraft({ ...draft, webPrice: value })}
            hint="Every size of it sells at this on the web shop — above or below the store price. The store price is not shown online while it applies, and the MSRP is struck through beside it when higher. Blank: the store price."
          />
          <div className="field">
            <span className="field__label" id="web-price-days">
              From and to (optional)
            </span>
            <span className="style-form__days">
              <DateInput
                aria-label="Online price from"
                aria-describedby="web-price-days"
                value={draft.webPriceStartsOn}
                onChange={(value) => setDraft({ ...draft, webPriceStartsOn: value })}
              />
              <DateInput
                aria-label="Online price to"
                aria-describedby="web-price-days"
                min={draft.webPriceStartsOn || undefined}
                value={draft.webPriceEndsOn}
                onChange={(value) => setDraft({ ...draft, webPriceEndsOn: value })}
              />
            </span>
            <p className="field__hint">
              {webPrice.cents === null
                ? 'The days an online price applies, both included. Blank: from now on.'
                : webPriceInEffect(webPrice, tillDay())
                  ? 'Selling online at this price now.'
                  : 'Not in effect today — online is the store price until its first day, or again after its last.'}
            </p>
          </div>
        </div>
      </Card>

      {save.isError ? <Callout tone="danger">{errorMessage(save.error)}</Callout> : null}

      <Modal
        open={confirmingDelete}
        tone="danger"
        title={`Delete ${product.name}?`}
        description="Every color and size of it goes too. Anything with sales, stock or orders against it cannot be deleted — make it inactive instead."
        onClose={() => setConfirmingDelete(false)}
        secondaryAction={
          <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
            Keep it
          </Button>
        }
        primaryAction={
          <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
            Delete permanently
          </Button>
        }
      >
        {remove.isError ? <Callout tone="danger">{errorMessage(remove.error)}</Callout> : null}
      </Modal>
    </Screen>
  );
}

// ---------------------------------------------------------------------------

/**
 * One variant, in full.
 *
 * The price grid on the style detail edits the number that changes weekly;
 * everything else about a single size lives here, where it can be seen at once
 * rather than as a twelfth column nobody can read.
 */
export function VariantSettingsDialog({
  productId,
  variant,
  onClose,
  onSaved,
}: {
  productId: string;
  variant: Variant;
  onClose: () => void;
  onSaved: (detail: Detail) => void;
}) {
  const notify = useNotice();
  const [draft, setDraft] = useState(() => ({
    msrp: variant.msrpCents === null ? '' : (variant.msrpCents / 100).toFixed(2),
    cost: (variant.costCents / 100).toFixed(2),
    retail: (variant.retailCents / 100).toFixed(2),
    target: variant.targetQty === null ? '' : String(variant.targetQty),
    min: variant.minQty === null ? '' : String(variant.minQty),
    bin: variant.binLocation ?? '',
    mfr: variant.vendorPartNumber ?? '',
    length: variant.altSize ?? '',
    active: variant.active,
  }));
  const [barcode, setBarcode] = useState('');
  /** A new SKU being typed, or null while the SKU is only shown. */
  const [newSku, setNewSku] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  /** Stock has come in at a cost, so the cost is corrected, never typed over (decision 126). */
  const [correctingCost, setCorrectingCost] = useState(false);

  const settled = (detail: Detail) => {
    onSaved(detail);
    onClose();
  };
  /** "Red · M", for a notice saying which size was saved. */
  const sizeWords = [variant.colorName, variant.sizeLabel]
    .filter((part) => part != null)
    .join(' · ');
  const sizeNamed = (styleName: string) =>
    sizeWords === '' ? styleName : `${styleName} ${sizeWords}`;

  // Derived first, because the mutations below read them. Declaring them after
  // works only by accident of when the closure runs.
  const retail = tryParseMoney(draft.retail) ?? variant.retailCents;
  const cost = variant.costFromStock
    ? variant.costCents
    : (tryParseMoney(draft.cost) ?? variant.costCents);
  const costChanged = !variant.costFromStock && cost !== variant.costCents;
  const margin = marginPercent(cost, retail);
  const unparseable = [draft.retail, ...(variant.costFromStock ? [] : [draft.cost])].some(
    (value) => value.trim() !== '' && tryParseMoney(value) === null,
  );

  const save = useMutation({
    mutationFn: () =>
      api.products.updateVariants.mutate({
        productId,
        patches: [
          {
            variantId: variant.id,
            retailCents: tryParseMoney(draft.retail) ?? variant.retailCents,
            msrpCents: draft.msrp.trim() === '' ? null : (tryParseMoney(draft.msrp) ?? null),
            // Only sent when it actually changed, so an untouched form never
            // trips the "stock has been received" guard.
            ...(costChanged ? { costCents: tryParseMoney(draft.cost) ?? variant.costCents } : {}),
            targetQty: draft.target.trim() === '' ? null : Number.parseInt(draft.target, 10),
            minQty: draft.min.trim() === '' ? null : Number.parseInt(draft.min, 10),
            binLocation: draft.bin.trim() === '' ? null : draft.bin.trim(),
            vendorPartNumber: draft.mfr.trim() === '' ? null : draft.mfr.trim(),
            // Only when it changed: it is part of what the size is.
            ...(draft.length.trim() !== (variant.altSize ?? '')
              ? { altSize: draft.length.trim() === '' ? null : draft.length.trim() }
              : {}),
            active: draft.active,
          },
        ],
      }),
    onSuccess: (detail) => {
      notify({ title: `Saved — ${sizeNamed(detail.product.name)}` });
      settled(detail);
    },
  });

  const addBarcode = useMutation({
    mutationFn: () =>
      api.products.setBarcode.mutate({ productId, variantId: variant.id, code: barcode.trim() }),
    onSuccess: (detail) => {
      setBarcode('');
      onSaved(detail);
    },
  });

  /*
   * A new SKU, typed or the next free one (decision 207). The old number stays
   * on the size as a barcode, so labels already on the merchandise scan.
   */
  const changeSku = useMutation({
    mutationFn: (wanted: { sku: string } | { next: true }) =>
      api.products.changeSku.mutate({ productId, variantId: variant.id, ...wanted }),
    onSuccess: (result) => {
      setNewSku(null);
      if (result.from !== result.to) {
        notify({
          title: `New SKU — ${sizeNamed(result.product.product.name)}, ${result.to}`,
          detail: `${result.from} still scans as this size, so labels already printed work.`,
        });
      }
      onSaved(result.product);
    },
  });

  const removeBarcode = useMutation({
    mutationFn: (code: string) =>
      api.products.removeBarcode.mutate({ productId, variantId: variant.id, code }),
    onSuccess: onSaved,
  });

  const remove = useMutation({
    mutationFn: () => api.products.deleteVariant.mutate({ productId, variantId: variant.id }),
    onSuccess: (detail) => {
      notify({ title: `Deleted — ${sizeNamed(detail.product.name)}` });
      settled(detail);
    },
  });

  const label = sizeWords;

  if (correctingCost) {
    return (
      <CorrectCostDialog
        variantId={variant.id}
        label={label === '' ? variant.sku : label}
        costCents={variant.costCents}
        onHand={variant.stock.onHand}
        onClose={() => setCorrectingCost(false)}
        onCorrected={() => {
          // Back to the size, showing the corrected cost from the server.
          void api.products.get.query({ productId }).then((detail) => {
            onSaved(detail);
            setCorrectingCost(false);
          });
        }}
      />
    );
  }

  return (
    <Modal
      open
      wide
      title={label === '' ? variant.sku : label}
      description={`${variant.sku} · ${variant.stock.onHand} on hand`}
      onClose={onClose}
      secondaryAction={
        <>
          <Button variant="danger" onClick={() => setConfirmingDelete(true)}>
            Delete
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={save.isPending}
          disabledReason={unparseable ? 'One of those is not an amount' : undefined}
          onClick={() => save.mutate()}
        >
          Save
        </Button>
      }
    >
      {confirmingDelete ? (
        <Callout tone="danger" title={`Delete ${variant.sku}?`}>
          <p>
            This cannot be undone. An item that has been sold, received or counted cannot be deleted
            at all — make it inactive instead.
          </p>
          <div className="variant-confirm">
            <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
              Keep it
            </Button>
            <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
              Delete permanently
            </Button>
          </div>
          {remove.isError ? <p className="variant-error">{errorMessage(remove.error)}</p> : null}
        </Callout>
      ) : null}

      <div className="field">
        <span className="field__label">SKU</span>
        {newSku === null ? (
          <span className="sku-change">
            <Mono>{variant.sku}</Mono>
            <Button variant="secondary" onClick={() => setNewSku('')}>
              Change SKU
            </Button>
            <Button
              variant="ghost"
              busy={changeSku.isPending}
              onClick={() => changeSku.mutate({ next: true })}
            >
              New number
            </Button>
          </span>
        ) : (
          <span className="sku-change">
            <input
              className="input code"
              aria-label="New SKU"
              inputMode="numeric"
              autoFocus
              maxLength={6}
              placeholder="Six digits"
              value={newSku}
              onChange={(event) => setNewSku(event.target.value.replace(/\D/g, ''))}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && newSku.length === 6) {
                  event.preventDefault();
                  changeSku.mutate({ sku: newSku });
                }
              }}
            />
            <Button
              variant="secondary"
              busy={changeSku.isPending}
              disabledReason={newSku.length !== 6 ? 'A SKU is six digits' : undefined}
              onClick={() => changeSku.mutate({ sku: newSku })}
            >
              Use this SKU
            </Button>
            <Button variant="ghost" onClick={() => setNewSku(null)}>
              Keep {variant.sku}
            </Button>
          </span>
        )}
        <p className="field__hint">
          The old number keeps scanning as this size, so labels already printed still work.
        </p>
        {changeSku.isError ? (
          <Callout tone="danger">{errorMessage(changeSku.error)}</Callout>
        ) : null}
      </div>

      <div className="style-form">
        {variant.costFromStock ? (
          <div className="field">
            <span className="field__label">Cost each</span>
            <span className="cost-fixed">
              <span className="money">{money(variant.costCents)}</span>
              <Button variant="secondary" onClick={() => setCorrectingCost(true)}>
                Correct cost
              </Button>
            </span>
            <p className="field__hint">
              The average of what the stock cost. Correct it if a delivery came in at the wrong
              price.
            </p>
          </div>
        ) : (
          <Field
            label="Cost each"
            value={draft.cost}
            onChange={(value) => setDraft({ ...draft, cost: value })}
            hint="Type it until stock comes in; after that it is the average of what the stock cost."
          />
        )}
        <Field
          label="MSRP"
          value={draft.msrp}
          onChange={(value) => setDraft({ ...draft, msrp: value })}
        />
        <Field
          label="Retail"
          value={draft.retail}
          onChange={(value) => setDraft({ ...draft, retail: value })}
        />
      </div>

      {margin !== null ? (
        <p className={`text-secondary${margin < 0 ? ' margin--negative' : ''}`}>
          {money(retail)} on {money(cost)} cost is {margin.toFixed(0)}% margin.
        </p>
      ) : null}

      <CostHistory variantId={variant.id} />

      <div className="style-form">
        <Field
          label="Target quantity"
          value={draft.target}
          onChange={(value) => setDraft({ ...draft, target: value })}
          hint="What a full rack looks like. Open to buy and a reorder work up to this."
        />
        <Field
          label="Minimum"
          value={draft.min}
          onChange={(value) => setDraft({ ...draft, min: value })}
          hint="When on hand plus on order falls below this, the size shows in Reports → Reorder."
        />
        <Field
          label="Bin location"
          value={draft.bin}
          onChange={(value) => setDraft({ ...draft, bin: value })}
          placeholder="Back wall, rack 3"
        />
        {/* FB-1079: the maker's number, filled from the vendor's export where it
            had one, shown and kept here, and printed on purchase orders. */}
        <Field
          label="Length"
          value={draft.length}
          onChange={(value) => setDraft({ ...draft, length: value })}
          placeholder="Short, Regular, Long"
          hint="For a model that comes in lengths — a size made before them is given one here. Past receipts keep what they said."
        />
        <Field
          label="Mfr #"
          value={draft.mfr}
          onChange={(value) => setDraft({ ...draft, mfr: value })}
          hint="The maker's own number for this size. Printed on purchase orders."
        />
      </div>

      <div className="field">
        <span className="field__label">Barcodes</span>
        <div className="barcodes">
          {variant.barcode === null ? (
            <span className="text-secondary">
              No maker's barcode (UPC). It still scans from its own label, and its SKU can be typed
              at the register.
            </span>
          ) : (
            <span className="tag">
              <Mono>{variant.barcode}</Mono>
              <Badge tone="neutral">Primary</Badge>
              <button
                type="button"
                className="tag__remove"
                aria-label={`Remove barcode ${variant.barcode}`}
                disabled={removeBarcode.isPending}
                onClick={() => removeBarcode.mutate(variant.barcode!)}
              >
                ×
              </button>
            </span>
          )}
          {/* Earlier SKUs and any other code kept on this size: each still scans as it. */}
          {variant.otherBarcodes.map((code) => (
            <span className="tag" key={code}>
              <Mono>{code}</Mono>
              <Badge tone="neutral">Also scans</Badge>
              <button
                type="button"
                className="tag__remove"
                aria-label={`Remove barcode ${code}`}
                disabled={removeBarcode.isPending}
                onClick={() => removeBarcode.mutate(code)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="barcode-add">
          <input
            className="input code"
            placeholder="Scan or type a barcode"
            value={barcode}
            autoComplete="off"
            onChange={(event) => setBarcode(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && barcode.trim().length >= 4) {
                event.preventDefault();
                addBarcode.mutate();
              }
            }}
          />
          <Button
            variant="secondary"
            busy={addBarcode.isPending}
            disabledReason={barcode.trim().length < 4 ? 'Scan or type a barcode' : undefined}
            onClick={() => addBarcode.mutate()}
          >
            Add
          </Button>
        </div>
        {addBarcode.isError ? (
          <Callout tone="danger">{errorMessage(addBarcode.error)}</Callout>
        ) : null}
      </div>

      <label className="toggle">
        <input
          type="checkbox"
          checked={!draft.active}
          onChange={(event) => setDraft({ ...draft, active: !event.target.checked })}
        />
        <span>Inactive — off the register and out of search, history left intact</span>
      </label>

      {save.isError ? <Callout tone="danger">{errorMessage(save.error)}</Callout> : null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * What each receipt actually paid for this SKU (revision item 18).
 *
 * The March $10 and the May $20 both survive in `receiving_order_lines`; this
 * is finally somewhere to *see* them, with the weighted average they feed
 * beside them. There is deliberately no reconstructed running-average column
 * — replaying the formula needs the on-hand at each receipt, which sales in
 * between changed, and a figure that looks exact and is not is worse than its
 * absence (§2.2-10).
 */
function CostHistory({ variantId }: { variantId: string }) {
  const history = useQuery({
    queryKey: ['products', 'costHistory', variantId],
    queryFn: () => api.products.costHistory.query({ variantId }),
  });

  if (history.data === undefined || history.data.receipts.length === 0) return null;

  return (
    <div className="field">
      <span className="field__label">Cost history</span>
      <table className="table cost-history">
        <thead>
          <tr>
            <th>Received</th>
            <th>PO</th>
            <th className="num">Units</th>
            <th className="num">Paid each</th>
          </tr>
        </thead>
        <tbody>
          {history.data.receipts.map((receipt) => (
            <tr key={`${receipt.receiptNumber}-${receipt.receivedAt ?? ''}`}>
              <td>
                {receipt.receivedAt === null
                  ? '—'
                  : new Date(receipt.receivedAt).toLocaleDateString()}
              </td>
              <td>
                <Mono>{receipt.poNumber ?? receipt.receiptNumber}</Mono>
              </td>
              <td className="num">{receipt.quantity}</td>
              <td className="num money">{money(receipt.unitCostCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="field__hint">
        Every receipt keeps what it actually paid; the {money(history.data.averageCostCents)} cost
        above is their average, recalculated at each delivery.
      </p>
    </div>
  );
}

export function Field({
  label,
  value,
  onChange,
  placeholder,
  hint,
  mono = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  mono?: boolean;
}) {
  const id = `settings-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={`input${mono ? ' code' : ''}`}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
      />
      {hint !== undefined ? <p className="field__hint">{hint}</p> : null}
    </div>
  );
}

export function Picker({
  label,
  value,
  onChange,
  options,
  optional = false,
  searchable = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  optional?: boolean;
  /** Type to narrow it — for a list long enough that scrolling is the slow part (2026-10-02). */
  searchable?: boolean;
}) {
  const id = `settings-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {searchable ? (
        <SearchSelect
          id={id}
          value={value}
          onChange={onChange}
          placeholder={`Pick a ${label.toLowerCase()} — type to search`}
          {...(optional ? { noneLabel: 'None' } : {})}
          options={options}
        />
      ) : (
        <select
          id={id}
          className="select"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">{optional ? 'None' : `Pick a ${label.toLowerCase()}`}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/** Grams as pounds to two places — the unit a US shop weighs a box in. */
function poundsFrom(grams: number): string {
  return String(Math.round((grams / 453.592) * 100) / 100);
}

/** Pounds as typed, to whole grams; null when it is not a positive number. */
function gramsFrom(pounds: string): number | null {
  const text = pounds.trim();
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const grams = Math.round(Number(text) * 453.592);
  return grams > 0 ? grams : null;
}
